import { Check, ChevronLeft, ChevronRight, ClipboardList } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { errText } from "@/components/prospect/common";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const fillVars = (text, row) => (text || "").replace(/\{(nom|ville|commune|date)\}/g, (_, k) => (k === "date" ? new Date().toLocaleDateString("fr-FR") : k === "nom" ? row.name : row.city) || "");

const QuestionInput = ({ q, value, onChange }) => {
  if (q.type === "text") return <textarea rows={2} className="ps-sr-text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`script-q-${q.id}`} />;
  if (q.type === "number") return <input type="number" className="ps-sr-text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`script-q-${q.id}`} />;
  const multi = q.type === "multi";
  const picked = multi ? value || [] : [value];
  const pick = (o) => onChange(multi ? (picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o]) : value === o ? "" : o);
  return (
    <div className="ps-sr-options" data-testid={`script-q-${q.id}`}>
      {q.options.map((o, i) => <button key={o} type="button" className={picked.includes(o) ? "on" : ""} onClick={() => pick(o)} data-testid={`script-q-${q.id}-opt-${i}`}>{picked.includes(o) && <Check size={14} />}{o}</button>)}
    </div>
  );
};

export function CallScriptRunner({ scripts, row, fileId, onDone, onClose }) {
  const [script, setScript] = useState(scripts[0]);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const step = script.steps[i];
  const last = i === script.steps.length - 1;
  const set = (qid, v) => setAnswers({ ...answers, [qid]: v });
  const done = (s) => s.questions.length && s.questions.every((q) => (Array.isArray(answers[q.id]) ? answers[q.id].length : answers[q.id]));
  const submit = async () => {
    const list = script.steps.flatMap((s) => s.questions.map((q) => ({ question: q.label, answer: [].concat(answers[q.id] || []).join(", "), field: q.field || null }))).filter((a) => a.answer);
    setBusy(true);
    try {
      const { data } = await api.post(`/prospect/files/${fileId}/rows/${row.id}/script-answers`, { script_id: script.id, script_name: script.name, answers: list });
      toast.success(data.sent_to_lead ? "Réponses ajoutées en commentaire sur l'opportunité." : "Réponses enregistrées.");
      onDone({ script_id: script.id, script_name: script.name, answers: list, at: new Date().toISOString() });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="ps-sr" data-testid="script-runner">
        <DialogHeader className="ps-sr-head">
          <div>
            <DialogTitle><ClipboardList size={20} /> {script.name}</DialogTitle>
            <DialogDescription>{row.name}{row.city ? ` · ${row.city}` : ""} — cochez les réponses au fil de l'appel</DialogDescription>
          </div>
          {scripts.length > 1 && <select value={script.id} onChange={(e) => { setScript(scripts.find((s) => s.id === e.target.value)); setI(0); setAnswers({}); }} data-testid="script-runner-select">{scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}
        </DialogHeader>
        <div className="ps-sr-body">
          <ol className="ps-sr-steps">
            {script.steps.map((s, k) => <li key={s.id}><button type="button" className={`${k === i ? "on" : ""} ${done(s) ? "done" : ""}`} onClick={() => setI(k)} data-testid={`script-step-${k}`}><span>{done(s) ? <Check size={12} /> : k + 1}</span>{s.title}</button></li>)}
          </ol>
          <div className="ps-sr-main">
            <p className="ps-sr-progress">Étape {i + 1} / {script.steps.length}</p>
            <h3>{step.title}</h3>
            {step.say && <blockquote className="ps-sr-say" data-testid="script-say">{fillVars(step.say, row)}</blockquote>}
            {step.questions.map((q) => <div key={q.id} className="ps-sr-q"><p>{q.label}{q.field && <small> → {q.field}</small>}</p><QuestionInput q={q} value={answers[q.id]} onChange={(v) => set(q.id, v)} /></div>)}
            <div className="ps-sr-actions">
              <button type="button" className="secondary-button" disabled={!i} onClick={() => setI(i - 1)} data-testid="script-prev"><ChevronLeft size={15} /> Précédent</button>
              {last
                ? <button type="button" className="primary-button" disabled={busy} onClick={submit} data-testid="script-submit"><Check size={16} /> {busy ? "Enregistrement…" : "Valider les réponses"}</button>
                : <button type="button" className="primary-button" onClick={() => setI(i + 1)} data-testid="script-next">Suivant <ChevronRight size={15} /></button>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
